import { useCallback } from 'react'
import type { MemoTemplate } from '@/shared/templates/memos'
import { seedStart, shift } from '../lib/spanStart'
import { useSeededSpan } from './useSeededSpan'
import type { SeededSpan } from './useSeededSpan'

/** The days a memo draws on, and the two fields the editor moves them with. */
export interface MemoDateRange extends Omit<SeededSpan, 'shiftBy'> {
  /**
   * The first of the days the memo is *about*, which is what its titles and
   * sluglines name. Not the span above: reaching that back a day for the late
   * news is a search, and says nothing about which PM is being written, so a
   * morning PM keeps naming the day it belongs to however far it reaches. A
   * template that titles itself after a span the editor picks out says so with
   * `titleFollowsSpan`, and gets the span here instead.
   */
  titleDate: string
  /** The last of them, the way `lastDate` is the last of the span. */
  titleLastDate: string
  /**
   * Move the whole span onto the given weekday, `0` for Sunday the way
   * `Date#getDay` counts - how the dialog answers the editor picking one of a
   * template's shapes, a shape being the weekday its first day falls on.
   *
   * The span moves rather than being rebuilt: both ends travel the same number
   * of days, so a memo being written for next weekend stays there and a span
   * reached out by hand keeps its length. A span already opening on that
   * weekday is left alone, and goes on following the memo's day.
   */
  shiftToWeekday: (weekday: number) => void
}

/**
 * The days a memo draws its candidates from, and separately the days it is
 * about - neither of them the day the memo itself belongs to, which is
 * `memoDate` and does not move with either.
 *
 * Every template offers both ends of the span, pre-filled to the day the memo
 * belongs to plus whatever lead, start weekday and span it declares - see
 * `seedStart`. That is because a memo is regularly built out of two days'
 * material and the second day is not always ahead: the morning PM reaches back
 * for the night's late news, and picking from one day and then from the next is
 * a single memo rather than two. A template whose columns are about different
 * days draws on a span per column instead - see `useColumnDateRanges` - and
 * this one is then only what the titles are worked out from.
 *
 * Which is why the days the titles name are worked out here too, and by default
 * from the template and `memoDate` alone. Moving the span is then a search that
 * cannot retitle the PM, and only a template whose title names a span the editor
 * picks out - `titleFollowsSpan` - has the two coincide.
 *
 * Both ends of the span follow the memo's day until the editor takes them over -
 * see `useSeededSpan`.
 *
 * A template written in more than one shape is the one that moves the span the
 * other way round: the shape is read off the day the span opens on, so the
 * dialog answers a shape the editor picks by moving the span onto its weekday -
 * see `shiftToWeekday`.
 */
export function useMemoDateRange({
  template,
  templateId,
  memoDate,
  planningId
}: {
  template: MemoTemplate | undefined
  /**
   * The picked template's id, which is what a template change is noticed by -
   * `template` is undefined for an id no template answers to, and two of those
   * in a row are not the same template.
   */
  templateId: string
  /** The day the memo itself belongs to, which both ends are seeded from. */
  memoDate: string
  /** The planning the memo is filed into, if any. */
  planningId: string | undefined
}): MemoDateRange {
  const span = template?.defaultSpanDays ?? 0

  const { date, toDate, lastDate, validRange, setDate, setToDate, shiftBy } = useSeededSpan({
    start: seedStart(memoDate, template),
    span,
    templateId,
    planningId
  })

  // Where the span would sit had the editor left it alone, which is what the
  // titles name: the day the memo belongs to, the template's lead, and the
  // template's span. Worked out from the props rather than read off the fields,
  // so no amount of moving those can reach it.
  const titleDate = template?.titleFollowsSpan
    ? date
    : shift(memoDate, template?.leadDays ?? 0)
  const titleLastDate = template?.titleFollowsSpan
    ? lastDate
    : shift(titleDate, span)

  // Already the span's own weekday is no move at all - and no statement about
  // which days it covers, so the span goes on following the memo's day.
  const shiftToWeekday = useCallback((weekday: number) => {
    shiftBy(daysToWeekday(date, weekday))
  }, [date, shiftBy])

  return {
    date,
    toDate,
    lastDate,
    titleDate,
    titleLastDate,
    validRange,
    setDate,
    setToDate,
    shiftToWeekday
  }
}

/**
 * How far a `YYYY-MM-DD` day is from the given weekday, `0` for Sunday the way
 * `Date#getDay` counts: forwards or backwards, whichever is nearer, and `0` for
 * a day already falling on one.
 *
 * The nearest rather than the next, so that moving a span onto one weekday and
 * then back onto another lands it where it started rather than a week on - the
 * two shapes of a helg-PM are a day apart, and flipping between them covers the
 * same weekend both ways round.
 */
function daysToWeekday(day: string, weekday: number): number {
  const forward = (weekday - new Date(`${day}T00:00:00`).getDay() + 7) % 7

  return forward > 3 ? forward - 7 : forward
}
